import debounce from 'lodash/debounce';

import { optionalHeadersFor } from '@shell/utils/table-views/optional-headers';
import { isExtensionTable, isConfigurableTablesEnabled, TABLE_VIEWS_SHELL_KEY } from '@shell/utils/table-views/feature';
import { AGE } from '@shell/config/table-headers';
import { NotificationLevel } from '@shell/types/notifications';
import { downloadFile } from '@shell/utils/download';
import { exportColumnsFor, rowsToCsv, rowsToJson, rowsToYaml } from '@shell/utils/table-views/export';
import {
  LABEL_FIELD_PREFIX, coreFieldIdsFor, fieldsFor, findField, headerFieldId, holdsDates, isIgnoredColumn, serverPathFor, summaryToValues
} from '@shell/utils/table-views/fields';
import { applyQueryExpression } from '@shell/utils/table-views/filter-rows';
import { parseQuery, parseQueryExpression, queryFor } from '@shell/utils/table-views/query';
import { queryToServerFilters } from '@shell/utils/table-views/server-filters';
import { expandQuery } from '@shell/utils/table-views/query-fields';
import { SEARCH_DEBOUNCE } from '@shell/config/search';
import { NAME_FILTER_QUERY, STATE_FILTER_QUERY } from '@shell/config/query-params';
import { TABLE_VIEWS } from '@shell/store/prefs';
import { DEFAULT_MANDATORY_SORT } from '@shell/components/SortableTable/sorting';
import { sortBy } from '@shell/utils/sort';
import { uniq } from '@shell/utils/array';
import { NO_GROUPING, savedViewsByType } from '@shell/utils/table-views/views';
import { defaultViewIdFor, globalPageKey, globalViewsFor } from '@shell/utils/table-views/global';
import { UI } from '@shell/config/types';

/** Most rows an "all matching" export will fetch - a limit on what the browser holds, not on the api */
const EXPORT_ROW_LIMIT = 50000;

/** Lower for YAML, which is a request per resource and can't be called off once started */
const EXPORT_ROW_LIMIT_YAML = 10000;

/** Fetched a page at a time so there is progress to report */
const EXPORT_PAGE_SIZE = 1000;

const VIEW_SWITCH_TIMEOUT = 8000;

/** A field's values are asked for again as its suggestions open, but not within this of the last answer */
const FIELD_VALUES_REFRESH_GAP = 2000;

/** Marks a view's grouping as one of the table's own rather than a column */
export const TABLE_GROUPING_PREFIX = 'group:';

/** Marks a view's grouping as a date column's, by month */
export const MONTH_GROUPING_PREFIX = 'month:';

/**
 * The table's saved views, default and tab place: its page's, under the type, when it has one, else
 * the type's own. The first shape views were kept in was the bare list. Outside the component so
 * `data` can ask
 */
function savedViewsEntry(vm) {
  let stored;

  // An older Rancher running an extension built with this shell has no such preference, and its store throws
  try {
    stored = savedViewsByType(vm.$store.getters['prefs/get'](TABLE_VIEWS))[vm.schema?.id];
  } catch {
    return undefined;
  }

  const typeEntry = Array.isArray(stored) ? { views: stored } : stored;

  return vm.tableViewsPage ? typeEntry?.pages?.[vm.tableViewsPage] : typeEntry;
}

/**
 * The table's views shared with everyone, as far as the store has them; the tabs load them. Outside
 * the component so `data` can ask
 */
function globalViewsOf(vm) {
  const getters = vm.$store.getters;
  const type = UI.TABLE_CONFIGURATION;
  const configs = getters['management/schemaFor']?.(type) && getters['management/haveAll'](type) ? getters['management/all'](type) : [];

  return globalViewsFor(configs, globalPageKey(vm.schema?.id || '', vm.tableViewsPage));
}

/** Every view the table's tabs show: shared, then the user's */
function allViewsOf(vm, saved = savedViewsEntry(vm), shared = globalViewsOf(vm)) {
  const sharedViews = shared.views.map((entry) => entry.view);
  const sharedIds = new Set(sharedViews.map((view) => view.id));

  return sharedViews.concat((saved?.views || []).filter((view) => !sharedIds.has(view.id)));
}

/** Whether a table would show the saved view tabs, table views allowing. Outside the component so `data` can ask */
function wantsTableViewTabs(vm) {
  if (vm.tableViewTabs !== null && vm.tableViewTabs !== undefined) {
    return vm.tableViewTabs;
  }

  // The cluster dashboard is routed by cluster, so its tables would look like list pages
  if (vm.providedShowTableViewTabs !== null && vm.providedShowTableViewTabs !== undefined) {
    return vm.providedShowTableViewTabs;
  }

  // Only the id: the cluster list is routed by the provisioning type while its table has the
  // management one
  return !vm.$route?.params?.id;
}

/**
 * The table views half of ResourceTable. Needs from its host: `schema`, `rows`, `headers`,
 * `namespaced`, `inStore`, `externalPaginationEnabled`, `externalPaginationResult`,
 * `externalPaginationArgs`, `externalPaginationScope`, `hasAdvancedFiltering`, `groupBy`,
 * `tableGroupings` and `defaultGroupBy`
 */
export default {
  /** Supplied by pages that put tables under tabs of their own. The `tableViewTabs` prop still wins */
  inject: {
    providedShowTableViewTabs: { from: 'showTableViewTabs', default: null },
    providedTableViewsShell:   { from: TABLE_VIEWS_SHELL_KEY, default: null },
  },

  props: {
    /** Force the saved view tabs on or off. Null works it out - see showTableViewTabs */
    tableViewTabs: {
      type:    Boolean,
      default: null
    },

    /** Show the table views toolbar. Null means on for any table showing a known resource type */
    tableViews: {
      type:    Boolean,
      default: null,
    },

    /**
     * A page keeping saved views of its own, eg `home`. They live under the resource type's entry,
     * beside the type's own views, which every other list of it shares
     */
    tableViewsPage: {
      type:    String,
      default: null,
    },

    /** Fields the query knows that aren't columns, eg `pinned` on a list of clusters */
    queryFields: {
      type:    Array,
      default: () => [],
    },
  },

  data() {
    /** @type {{ views: import('@shell/types/table-views').TableViewSaved[], defaultViewId: string|null }} */
    const saved = savedViewsEntry(this);
    const shared = globalViewsOf(this);
    const views = allViewsOf(this, saved, shared);
    const defaultId = defaultViewIdFor(saved?.defaultViewId, shared, views.map((view) => view.id));
    /**
     * A link to some of the list's states, eg from an overview's counts, opens the table's own tab
     * filtered to them, rather than the default view
     */
    const linkedQuery = wantsTableViewTabs(this) && isConfigurableTablesEnabled(this.$store) ? queryFor(this.$route?.query?.[STATE_FILTER_QUERY], this.$route?.query?.[NAME_FILTER_QUERY]) : '';
    /**
     * Only a table showing the saved view tabs opens on the default one. Another, eg a detail page's
     * Pods tab, would be filtered by a view it has no tab to show or leave
     *
     * @type {import('@shell/types/table-views').TableViewSaved|undefined}
     */
    const defaultView = wantsTableViewTabs(this) && !linkedQuery ? views.find((view) => view.id === defaultId) : undefined;

    return {
      /**
       * Needed because an empty default holds what the All tab holds, and two views can hold the
       * same config
       */
      openedViewId: linkedQuery ? null : defaultView?.id,

      /**
       * Opened by a link naming some of the list's states, whose parameter is still in the URL: kept
       * while the query is the one it opened with, so Back or a reload opens the list filtered again
       */
      stateLinkPending: !!linkedQuery,

      stateLinkQuery: linkedQuery,

      fieldValues: {},

      /** Ids of the fields whose values are being asked for */
      fieldValuesLoading: [],

      /** Ids of the fields whose values in hand are being asked for again, quietly */
      fieldValuesRefreshing: [],

      /** When each field's values last came, by id */
      fieldValuesAt: {},

      /** query -> rows matched, or null when the api wouldn't say */
      viewCounts: {},

      countingInFlight: false,

      recountPending: false,

      view: {
        query:          linkedQuery || defaultView?.query || '',
        columns:        defaultView?.columns || null,
        columnOrder:    defaultView?.columnOrder || null,
        labelColumns:   defaultView?.labelColumns || [],
        groupBy:        defaultView?.groupBy || null,
        sort:           defaultView?.sort || null,
        sortDescending: defaultView?.sortDescending || false,
      },

      /** Learned from the table's first sort report */
      defaultSort: null,

      /** What each tab is filtering by, including unsaved edits on tabs not in front */
      tabQueries: [],

      /**
       * The query the table acts on, which trails the one being typed: filtering, fetching and
       * counting are too expensive per keystroke. A saved view applied is flushed at once - see the
       * watcher
       */
      settledQuery: linkedQuery || defaultView?.query || '',

      /** Counts the views the tabs put on the table, so the search box starts its undo afresh on each */
      tabViewChanges: 0,

      debouncedSettleQuery: debounce(function(query) {
        this.settledQuery = query;
      }, SEARCH_DEBOUNCE),

      appliedViewFilters: [],

      // Starts empty so an initial empty query doesn't emit
      lastViewFiltersKey: '[]',

      /**
       * From a view's filters changing until its rows arrive, so one view's rows never show under
       * another's columns. Also turns alt loading off, which would leave the old view's rows up
       */
      viewSwitching: false,

      viewSwitchTimer: null,

      /** The request in hand when the switch began: its answer is the old view's, whatever it filters by */
      viewSwitchFromArgs: null,

      lastResultArgsKey: null,

      pendingViewFilters: [],

      supersededViewFilters: [],


      debouncedFetchViewCounts: debounce(() => this.fetchViewCounts(), 800),

      debouncedRefreshViewCounts: debounce(() => this.fetchViewCounts(true), 400),
    };
  },

  mounted() {
    if (this.showTableViews) {
      this.debouncedFetchViewCounts();
    }
  },

  beforeUnmount() {
    clearTimeout(this.viewSwitchTimer);
    this.debouncedSettleQuery.cancel();
  },

  watch: {
    /** Cached suggestions belong to the old scope */
    summaryBaseUrl(neu, old) {
      if (neu === old) {
        return;
      }

      // Re-fetched rather than dropped, or the input falls back to display values ("Active" rather
      // than "active")
      const known = Object.keys(this.fieldValues);

      this.fieldValues = {};
      known.forEach((fieldId) => this.fetchFieldValues(fieldId));
    },

    viewCountsKey() {
      this.debouncedFetchViewCounts();
    },

    queryFieldsKey(neu, old) {
      if (neu === old) {
        return;
      }

      // A tab counting `pinned:true` counts other clusters now
      this.fetchViewCounts(true);

      const known = (this.queryFields || []).filter((field) => this.fieldValues[field.id] !== undefined);

      if (!known.length) {
        return;
      }

      const fieldValues = { ...this.fieldValues };

      known.forEach((field) => delete fieldValues[field.id]);
      this.fieldValues = fieldValues;
      known.forEach((field) => this.fetchFieldValues(field.id));
    },

    /** Not tied to the rows, which would blink counts to zero on every fetch */
    viewCountsScope() {
      this.debouncedRefreshViewCounts();
    },

    'serverViewFilters.filters'(neu) {
      if (!this.serverSideTableViews) {
        return;
      }

      const filters = neu || [];
      const key = JSON.stringify(filters);

      if (key === this.lastViewFiltersKey) {
        return;
      }

      this.supersededViewFilters = this.pendingViewFilters;
      this.pendingViewFilters = filters;
      this.lastViewFiltersKey = key;
      this.beginViewSwitch();
      this.appliedViewFilters = filters.length ? filters : [];
    },

    /**
     * Only typing waits. A query arriving whole (a saved view) doesn't, and typing only changes the
     * end, so one query starting with the other tells the two apart. Clearing is a whole-query action
     * too: debouncing it lets a clear-and-retype of the same query never settle a change
     */
    'view.query'(neu, old) {
      const query = neu || '';
      const previous = old || '';

      if (this.stateLinkPending && query !== this.stateLinkQuery) {
        this.dropStateLink();
      }

      // Settled already, eg by a tab
      if (query === (this.settledQuery || '')) {
        this.debouncedSettleQuery.cancel();

        return;
      }

      const typed = query.startsWith(previous) || previous.startsWith(query);

      this.debouncedSettleQuery(query);

      if (!typed || !query) {
        this.debouncedSettleQuery.flush();
      }
    },

    /** Wait for the api's regrouped rows rather than regrouping the ones in hand and being corrected after */
    viewGroupSort(neu, old) {
      if (this.serverSideTableViews && neu !== old) {
        this.beginViewSwitch();
      }
    },

    'view.sort'() {
      this.$nextTick(() => this.applyViewSort());
    },

    'view.sortDescending'() {
      this.$nextTick(() => this.applyViewSort());
    },

    /** Hiding the column a view sorts by returns the table to its default sort */
    viewHeaders() {
      this.$nextTick(() => this.applyViewSort());
    },

    /**
     * Only the response this view asked for ends the wait; one already in flight answers first.
     * Watched rather than `rows`, which is filled in place and never changes identity
     */
    externalPaginationResult(neu) {
      if (this.viewFiltersApplied && this.externalPaginationArgs !== this.viewSwitchFromArgs) {
        this.endViewSwitch();
      }

      const argsKey = this.externalPaginationArgs ? JSON.stringify(this.externalPaginationArgs) : null;

      if (neu && argsKey && argsKey === this.lastResultArgsKey) {
        this.debouncedRefreshViewCounts();
      }
      this.lastResultArgsKey = argsKey;
    },

    /** Asked again for the page in hand, eg by a view sorting as the last one did: nothing more is coming */
    externalPaginationArgs(neu) {
      if (this.viewSwitching && neu !== this.viewSwitchFromArgs && this.viewFiltersApplied &&
        JSON.stringify(neu) === JSON.stringify(this.viewSwitchFromArgs)) {
        this.endViewSwitch();
      }
    },
  },

  computed: {
    /**
     * The flag is asked first: a page turning the toolbar on can't overrule an administrator
     * turning it off
     */
    showTableViews() {
      if (!isConfigurableTablesEnabled(this.$store)) {
        return false;
      }

      if (this.tableViews !== null) {
        return this.tableViews;
      }

      // An extension's table stays as it was written unless the extension asks for table views
      if (this.inExtension) {
        return false;
      }

      return !!this.schema?.id && !this.hasAdvancedFiltering;
    },

    inExtension() {
      return isExtensionTable({
        providedShell: this.providedTableViewsShell,
        route:         this.$route,
        extensions:    this.$store?.state?.$extension,
        singleProduct: !!this.$store?.getters?.['isSingleProduct'],
      });
    },


    /**
     * Saved views are per type, so they don't belong above a table that lists one resource's things
     * (eg a deployment's pods). A detail route names a resource; a list route doesn't
     */
    showTableViewTabs() {
      if (this.tableViewTabs !== null) {
        return this.tableViewTabs;
      }

      return this.showTableViews && wantsTableViewTabs(this);
    },


    /** The page's columns, then the optional ones its type registers (see optionalHeadersFor) */
    availableHeaders() {
      const own = this._headers || [];

      if (!this.schema) {
        return own;
      }

      const known = {};

      own.forEach((header) => {
        known[headerFieldId(header)] = true;
      });

      const extra = optionalHeadersFor(this.schema.id, this.$store, this.externalPaginationEnabled)
        .filter((header) => {
          const id = headerFieldId(header);

          if (isIgnoredColumn(header) || known[id]) {
            return false;
          }

          known[id] = true;

          return true;
        });

      if (!extra.length) {
        return own;
      }

      let at = own.length;

      for (let i = own.length - 1; i >= 0; i--) {
        if (!isIgnoredColumn(own[i])) {
          at = i + 1;
          break;
        }
      }

      const plain = extra.filter((header) => !header.insertBefore);
      const out = own.slice(0, at).concat(plain, own.slice(at));
      // Without the column it goes before, after the others added
      let fallback = at + plain.length;

      extra.filter((header) => header.insertBefore).forEach((header) => {
        const index = out.findIndex((existing) => existing.name === header.insertBefore);

        out.splice(index >= 0 ? index : fallback++, 0, header);
      });

      return out;
    },


    defaultColumnIds() {
      return (this._headers || []).filter((header) => !isIgnoredColumn(header)).map((header) => headerFieldId(header));
    },


    defaultSortColumnId() {
      const header = (this._headers || []).find((h) => h.defaultSort);

      return header ? headerFieldId(header) : null;
    },


    coreColumnIds() {
      return coreFieldIdsFor(this.defaultColumnIds, this.defaultSortColumnId);
    },


    /**
     * The columns as the pagination api defines them, or null when the list isn't paginated - see
     * serverPathFor
     */
    paginationHeaders() {
      if (!this.externalPaginationEnabled || !this.schema) {
        return null;
      }

      return this.$store.getters['type-map/headersFor'](this.schema, true)
        .concat(optionalHeadersFor(this.schema.id, this.$store, true));
    },


    viewFields() {
      return fieldsFor(this.availableHeaders, this.filteredRows, (key) => this.t(key), this.paginationHeaders);
    },


    /**
     * The columns and the fields that exist only for the query: what a query is read and applied with.
     * A column that searches with free text only while it is on the table, eg the description, is left
     * out of it otherwise; a term naming it still works
     */
    viewQueryFields() {
      const shown = new Set((this.viewHeaders || []).map((header) => headerFieldId(header)));
      const fields = this.viewFields.map((field) => (field.header?.freeTextWhenShown && !shown.has(field.id) ? { ...field, notInFreeText: true } : field));

      return this.queryFields?.length ? fields.concat(this.queryFields) : fields;
    },


    /**
     * The values the query suggests: the api's, plus those the query only fields bring. A server
     * side list counts those too - its page is no guide - so they wait for the api's count
     */
    viewFieldValues() {
      const fixed = this.serverSideTableViews ? [] : (this.queryFields || []).filter((field) => field.values);

      if (!fixed.length) {
        return this.fieldValues;
      }

      return fixed.reduce((out, field) => ({ ...out, [field.id]: field.values }), { ...this.fieldValues });
    },


    /**
     * The fields the query names whose values are on their way: every one while the rows load, then
     * those still being asked for
     */
    viewPendingFields() {
      const named = this.viewTerms.map((term) => term.field).filter(Boolean);

      if (this.loading) {
        return named;
      }

      return named.filter((id) => this.fieldValuesLoading.includes(id));
    },


    /** What the query only fields' values stand for, eg the pinned ids: counts go stale when it changes */
    queryFieldsKey() {
      return JSON.stringify((this.queryFields || [])
        .filter((field) => field.values && field.expand)
        .map((field) => field.values.map(({ value }) => field.expand([{
          field: field.id, value, negated: false
        }]))));
    },


    /** `header-right` is left out: this renders its own there, and a duplicate would win */
    passthroughSlots() {
      const { 'header-right': headerRight, ...rest } = this.$slots;

      // A page's own group heading is drawn for its own grouping, not for a column's
      if (this.viewGroupField) {
        delete rest['group-by'];
      }

      return (this.showGrouping || this.showTableViews) ? rest : this.$slots;
    },


    /**
     * The table's own groupings, then the columns the table can sort by - so the group menu and the
     * sortable headers always agree. A column one of those groupings already covers is left out
     */
    viewGroupFields() {
      const own = this.tableGroupings.map((option) => ({
        id:      `${ TABLE_GROUPING_PREFIX }${ option.value }`,
        label:   this.tableGroupingLabel(option),
        isLabel: false,
      }));
      const covered = this.tableGroupings.flatMap((option) => [option.hideColumn, option.field]).filter(Boolean);

      const sortable = new Set(this.viewSortableFields.filter((field) => field.isLabel || !(covered.includes(field.header?.name) || covered.includes(field.header?.value))));
      // A date gathers nothing - nearly every row has its own - so it is grouped by month, in its place
      const dates = new Set(this.viewDateFields);

      return own.concat(this.viewFields
        .filter((field) => sortable.has(field) || dates.has(field))
        .map((field) => (dates.has(field) ? this.monthGrouping(field) : field)));
    },

    /**
     * The columns the table can sort by, labels included, less the dates. On a server side list, only
     * those the api can be asked about: grouping by anything else could only regroup the page in hand
     */
    viewSortableFields() {
      return this.viewFields.filter((field) => {
        if (field.isLabel) {
          return true;
        }

        const { header } = field;

        if (!header?.sort || field.isDate || (this.serverSideTableViews && !serverPathFor(field))) {
          return false;
        }

        return typeof header.value === 'function' || !!header.value ||
          typeof header.sort === 'string' || (Array.isArray(header.sort) && typeof header.sort[0] === 'string');
      });
    },

    /** The table's own grouping the view has picked, if it picked one */
    viewTableGrouping() {
      const groupBy = this.groupByOf(this.view);

      return this.tableGroupings.find((option) => `${ TABLE_GROUPING_PREFIX }${ option.value }` === groupBy) || null;
    },



    /**
     * The columns the table sorts by - the same set Group By offers, so the two never disagree - and
     * the dates, offered by year and month. Labels can be filtered, but only the rows in hand could
     * list their keys, and that list empties as you type, so they are typed
     */
    viewFilterFields() {
      const offered = [...this.viewSortableFields, ...this.viewDateFields];
      const fields = this.viewFields.filter((field) => !field.isLabel && offered.includes(field));
      const queryFields = (this.queryFields || []).filter((field) => field.values);
      // Query only fields, eg Pinned, follow the name
      const at = fields.findIndex((field) => field.id === 'name') + 1 || fields.length;

      return [...fields.slice(0, at), ...queryFields, ...fields.slice(at)];
    },

    /** The date columns, which the query offers by year and month and Group By leaves out */
    viewDateFields() {
      return this.viewFields.filter((field) => {
        // Only a column with dates to offer: some hold text such as "23m", some nothing yet
        if (!field.isDate || !field.header?.sort || !holdsDates(this.filteredRows, field)) {
          return false;
        }

        if (!this.serverSideTableViews) {
          return true;
        }

        // A printed column the api holds only as relative text ("23m"), which no date can match
        const path = serverPathFor(field);

        return typeof path === 'string' && !path.startsWith('metadata.fields.');
      });
    },

    viewDateFieldIds() {
      return this.viewDateFields.map((field) => field.id);
    },


    viewTerms() {
      return parseQuery(this.settledQuery, this.viewQueryFields);
    },


    /** The query as clauses and groups - what filters, here and at the api */
    viewQuery() {
      return this.parseViewQuery(this.settledQuery);
    },


    /** The rows on screen are not yet the query's: it is still settling, or its rows are on the way */
    viewBusy() {
      return this.showTableViews && ((this.view.query || '') !== (this.settledQuery || '') || this.viewSwitching);
    },


    serverSideTableViews() {
      return this.showTableViews && this.externalPaginationEnabled && !!this.schema;
    },


    /** Is every filter this view wants applied, and none it replaced? */
    viewFiltersApplied() {
      const applied = (this.externalPaginationArgs?.filters || []).map((filter) => JSON.stringify(filter));
      const wanted = (this.pendingViewFilters || []).map((filter) => JSON.stringify(filter));
      const superseded = (this.supersededViewFilters || []).map((filter) => JSON.stringify(filter));

      return wanted.every((filter) => applied.includes(filter)) &&
        !superseded.some((filter) => !wanted.includes(filter) && applied.includes(filter));
    },


    serverViewFilters() {
      if (!this.serverSideTableViews) {
        return { filters: [], unsupported: [] };
      }

      return queryToServerFilters(this.viewQuery, this.viewQueryFields);
    },


    /** Names of the query's fields this list can't filter by, which are dropped rather than applied */
    unsupportedViewFields() {
      const seen = {};

      (this.serverViewFilters.unsupported || []).forEach((term) => {
        const field = term.field ? findField(this.viewQueryFields, term.field) : null;
        const label = field ? (field.label || field.id) : term.value;

        if (label) {
          seen[label] = true;
        }
      });

      return Object.keys(seen);
    },

    /** The list's own filters without the view's. Compared by value: they are rebuilt every render */
    listScopeFilters() {
      if (this.externalPaginationScope) {
        return this.externalPaginationScope.filters || [];
      }

      // No explicit scope, from an older caller
      const args = this.externalPaginationArgs;

      if (!args?.filters?.length) {
        return [];
      }

      const own = this.serverViewFilters.filters.map((filter) => JSON.stringify(filter));

      return args.filters.filter((filter) => !own.includes(JSON.stringify(filter)));
    },


    listScopeNamespaces() {
      return this.externalPaginationScope?.projectsOrNamespaces || this.externalPaginationArgs?.projectsOrNamespaces || [];
    },


    /** Only a server side filtered list asks the api for values; the rest read them off their rows */
    summaryBaseUrl() {
      // Some lists show a type their store has no schema for, and asking it for a url throws
      if (!this.serverSideTableViews) {
        return null;
      }

      const urlFor = this.$store.getters[`${ this.inStore }/urlFor`];
      const args = this.externalPaginationArgs;

      if (!args) {
        return urlFor(this.schema.id);
      }

      // No page: `summaryonly` still aggregates over the page window
      return urlFor(this.schema.id, null, {
        pagination: {
          filters:              this.listScopeFilters,
          projectsOrNamespaces: this.listScopeNamespaces,
        }
      });
    },


    viewRows() {
      if (this.serverSideTableViews) {
        return this.filteredRows;
      }

      if (!this.showTableViews || !this.viewTerms.length) {
        return this.filteredRows;
      }

      return applyQueryExpression(this.filteredRows, this.viewQuery, this.viewQueryFields);
    },


    /** The server's total across pages, or the filtered rows client side */
    viewMatchCount() {
      if (this.serverSideTableViews) {
        return this.externalPaginationResult?.count ?? this.filteredRows.length;
      }

      return this.viewRows.length;
    },


    viewGroupField() {
      const groupBy = this.groupByOf(this.view);

      if (!this.showTableViews || !groupBy) {
        return null;
      }

      return this.groupFieldFor(groupBy);
    },


    /**
     * The path behind the view's grouping. `computedGroupBy` is a function, which can't join the
     * sort, so this puts the grouping into the server's sort
     */
    viewGroupSort() {
      return (this.viewGroupField && this.groupSortFor(this.viewGroupField)) || this.groupSort;
    },


    viewHeaders() {
      return this.headersForView(this.view);
    },


    savedViews() {
      return allViewsOf(this);
    },

    /** Queries rather than views: two views filtering alike share one count */
    countableQueries() {
      const out = ['']
        .concat(this.savedViews.map((view) => view.query || ''))
        .concat([this.view.query || ''])
        // A tab with unsaved edits is filtering by them, not its saved query
        .concat(this.tabQueries);

      return Array.from(new Set(out));
    },


    viewCountsKey() {
      return JSON.stringify(this.countableQueries);
    },


    viewCountsScope() {
      return JSON.stringify(this.listScopeFilters) + JSON.stringify(this.listScopeNamespaces);
    },


    localViewCounts() {
      const out = {};

      this.countableQueries.forEach((query) => {
        const parsed = this.parseViewQuery(query);

        out[query] = parsed.clauses.length ? applyQueryExpression(this.filteredRows, parsed, this.viewQueryFields).length : this.filteredRows.length;
      });

      return out;
    },


    tabCounts() {
      return this.serverSideTableViews ? this.viewCounts : this.localViewCounts;
    },


    exportColumns() {
      return exportColumnsFor(this.viewHeaders, (key) => this.t(key));
    },
  },

  methods: {
    /**
     * A link naming some of the list's states leaves the URL once the query is changed from the one it
     * opened with: left, a reload after the query was cleared would filter the list again
     */
    dropStateLink() {
      if (!this.stateLinkPending || !this.showTableViews) {
        return;
      }

      this.stateLinkPending = false;

      if (!(STATE_FILTER_QUERY in (this.$route?.query || {}))) {
        return;
      }

      const query = { ...this.$route.query };

      delete query[STATE_FILTER_QUERY];
      delete query[NAME_FILTER_QUERY];

      // The route's guards run for it too, and can fail, eg with the session ending
      this.$router.replace({
        path: this.$route.path, query, hash: this.$route.hash
      }).catch(() => {});
    },

    /** The field a view's grouping names - a column, or a date column taken by month - or null */
    /** A query as the list applies it: fields that stand for others swapped for those */
    parseViewQuery(query) {
      return expandQuery(parseQueryExpression(query, this.viewQueryFields), this.viewQueryFields);
    },

    /** The grouping `view` is shown with: its own, else the one the table groups by by default */
    groupByOf(view) {
      if (view.groupBy === NO_GROUPING) {
        return null;
      }

      return view.groupBy || this.defaultGroupBy || null;
    },

    groupFieldFor(id) {
      if (id?.startsWith(MONTH_GROUPING_PREFIX)) {
        const date = findField(this.viewDateFields, id.slice(MONTH_GROUPING_PREFIX.length));

        return date ? this.monthGrouping(date) : null;
      }

      return findField(this.viewFields, id) || null;
    },

    /** A date column as the Group By entry that gathers its rows by month, under the column's own name */
    monthGrouping(field) {
      return {
        ...field, id: `${ MONTH_GROUPING_PREFIX }${ field.id }`, byMonth: true
      };
    },

    /** A short name for one of the table's own groupings: its `labelKey`, else the one matching its tooltip */
    tableGroupingLabel(option) {
      const short = option.labelKey || option.tooltipKey?.replace(/^resourceTable\.groupBy\./, 'tableViews.group.by.');

      if (short && this.$store.getters['i18n/exists'](short)) {
        return this.t(short);
      }

      return option.tooltipKey ? this.t(option.tooltipKey) : (option.tooltip || option.value);
    },

    /** A method as well as a computed, so a tab exported from its own menu gets its own headers */
    headersForView(view) {
      const headers = this._headers;

      if (!this.showTableViews) {
        return headers;
      }

      let out = headers;

      if (view.columns) {
        // From everything the type has, so the page's left out columns can be added. Core columns
        // always stay
        out = this.availableHeaders.filter((header) => isIgnoredColumn(header) || this.coreColumnIds.includes(headerFieldId(header)) || !this.viewFields.find((f) => !f.isLabel && f.id === headerFieldId(header)) || view.columns.includes(headerFieldId(header)));
      }

      if (view.columnOrder?.length) {
        // Only data columns move; `check`, `actions` and the rest stay put
        const order = view.columnOrder;
        const movable = out.filter((header) => !isIgnoredColumn(header) && order.includes(headerFieldId(header)));
        const sorted = movable.slice().sort((a, b) => order.indexOf(headerFieldId(a)) - order.indexOf(headerFieldId(b)));
        let next = 0;

        out = out.map((header) => (movable.includes(header) ? sorted[next++] : header));
      }

      if (view.labelColumns?.length) {
        out = out.slice();

        const ageIndex = out.findIndex((header) => header.name === AGE.name);
        const at = ageIndex >= 0 ? ageIndex : out.length;

        view.labelColumns.forEach((key, i) => {
          out.splice(at + i, 0, {
            name:   `${ LABEL_FIELD_PREFIX }${ key }`,
            label:  key,
            value:  (row) => row?.metadata?.labels?.[key] || '',
            sort:   false,
            search: false,
          });
        });
      }

      return out;
    },


    /**
     * The column's own sort path, so grouping orders rows as sorting by it would. Later `sort`
     * entries are tie breakers, and may carry `:desc`
     */
    groupSortFor(field) {
      const sort = field?.header?.sort;
      const first = Array.isArray(sort) ? sort[0] : sort;

      if (typeof first === 'string' && first) {
        return first.split(':')[0];
      }

      // Labels have no header, and some columns say nothing about sorting
      const path = field ? serverPathFor(field) : null;

      return typeof path === 'string' ? path : null;
    },


    /** The path a field's values are summarised on, or null for one whose values the query doesn't offer */
    fieldSummaryPath(field) {
      const raw = field ? serverPathFor(field) : null;
      // A column searched on several paths is summarised on its own
      const path = Array.isArray(raw) ? raw[0] : raw;
      // The query only offers values for these, so there is no point asking for another's
      const offered = this.viewFilterFields.some((f) => f.id === field?.id);

      return typeof path === 'string' && offered ? path : null;
    },

    /** Values in use for a field, from a steve summary: it counts every row without returning any */
    async summaryValues(fieldId, path) {
      const url = `${ this.summaryBaseUrl }&summary=${ encodeURIComponent(path) }&summaryonly`;
      const res = await this.$store.dispatch(`${ this.inStore }/request`, { opt: { url } });
      // Every timestamp, to count each month in full
      const max = this.viewDateFieldIds.includes(fieldId) ? Infinity : undefined;

      return summaryToValues(res, max);
    },

    async fetchFieldValues(fieldId) {
      if (!this.serverSideTableViews || this.fieldValues[fieldId] !== undefined) {
        return;
      }

      const field = findField(this.viewQueryFields, fieldId);

      // The api holds nothing to summarise, so each value is counted as a query on it
      if (field?.queryOnly) {
        return field.values ? this.fetchQueryFieldCounts(field) : undefined;
      }

      const path = this.fieldSummaryPath(field);

      if (!path) {
        // Claimed anyway, so the input stops asking and falls back to the page
        this.fieldValues = { ...this.fieldValues, [fieldId]: [] };

        return;
      }

      this.fieldValues = { ...this.fieldValues, [fieldId]: [] };
      this.fieldValuesLoading = [...this.fieldValuesLoading, fieldId];

      // The scope can change while the api answers, and the values are then asked for it afresh
      const base = this.summaryBaseUrl;

      try {
        const values = await this.summaryValues(fieldId, path);

        if (base === this.summaryBaseUrl) {
          this.fieldValues = { ...this.fieldValues, [fieldId]: values };
          this.fieldValuesAt = { ...this.fieldValuesAt, [fieldId]: Date.now() };
        }
      } catch (e) {
        // Not kept, so the field asks again the next time it's picked. Meanwhile the box offers the page's values
        if (base === this.summaryBaseUrl) {
          const { [fieldId]: failed, ...fieldValues } = this.fieldValues;

          this.fieldValues = fieldValues;
        }
      } finally {
        this.fieldValuesLoading = this.fieldValuesLoading.filter((id) => id !== fieldId);
      }
    },

    /**
     * Ask again for a field's values as its suggestions open, so they show what is there now: the ones
     * in hand stay offered meanwhile, with no loading, and are swapped for the answer. Not while it is
     * being asked already, nor again soon after an answer
     */
    async refreshFieldValues(fieldId) {
      const asked = this.fieldValuesLoading.includes(fieldId) || this.fieldValuesRefreshing.includes(fieldId);
      const recent = Date.now() - (this.fieldValuesAt[fieldId] || 0) < FIELD_VALUES_REFRESH_GAP;

      // Values not in hand yet are asked for the first time instead - see fetchFieldValues
      if (!this.serverSideTableViews || this.fieldValues[fieldId] === undefined || asked || recent) {
        return;
      }

      const path = this.fieldSummaryPath(findField(this.viewQueryFields, fieldId));

      if (!path) {
        return;
      }

      const base = this.summaryBaseUrl;

      this.fieldValuesRefreshing = [...this.fieldValuesRefreshing, fieldId];

      try {
        const values = await this.summaryValues(fieldId, path);

        if (base === this.summaryBaseUrl) {
          this.fieldValues = { ...this.fieldValues, [fieldId]: values };
          this.fieldValuesAt = { ...this.fieldValuesAt, [fieldId]: Date.now() };
        }
      } catch (e) {
        // The values in hand are still the best there is
      } finally {
        this.fieldValuesRefreshing = this.fieldValuesRefreshing.filter((id) => id !== fieldId);
      }
    },

    async fetchQueryFieldCounts(field) {
      this.fieldValues = { ...this.fieldValues, [field.id]: [] };
      this.fieldValuesLoading = [...this.fieldValuesLoading, field.id];

      try {
        const values = await Promise.all(field.values.map(async({ value }) => {
          const { filters } = queryToServerFilters(this.parseViewQuery(`${ field.id }:${ value }`), this.viewQueryFields);
          const res = await this.$store.dispatch(`${ this.inStore }/request`, { opt: { url: this.countUrl(filters) } });

          return { value, count: res?.count ?? res?.data?.length ?? 0 };
        }));

        this.fieldValues = { ...this.fieldValues, [field.id]: values };
      } catch (e) {
        // Still offered, counted as best the page can
        this.fieldValues = { ...this.fieldValues, [field.id]: field.values };
      } finally {
        this.fieldValuesLoading = this.fieldValuesLoading.filter((id) => id !== field.id);
      }
    },


    /**
     * Count each saved view's rows for its tab. Views the api can't express get no count rather
     * than a wrong one
     */
    async fetchViewCounts(refresh = false) {
      if (!this.serverSideTableViews) {
        return;
      }

      // A count stays until a fresh one replaces it, so none blink to zero
      const wanted = this.countableQueries.filter((query) => refresh || this.viewCounts[query] === undefined);

      if (!wanted.length) {
        return;
      }

      // One round at a time, or mid-flight triggers ask again and supersede their own requests
      if (this.countingInFlight) {
        this.recountPending = this.recountPending || refresh;

        return;
      }

      this.countingInFlight = true;

      try {
        await this.requestViewCounts(wanted);
      } finally {
        this.countingInFlight = false;
      }

      if (this.recountPending) {
        this.recountPending = false;
        await this.fetchViewCounts(true);
      }
    },


    async requestViewCounts(wanted) {
      await Promise.all(wanted.map(async(query) => {
        const parsed = this.parseViewQuery(query);
        const { filters, unsupported } = queryToServerFilters(parsed, this.viewQueryFields);

        if (unsupported.length) {
          this.viewCounts = { ...this.viewCounts, [query]: null };

          return;
        }

        try {
          const url = this.countUrl(filters);
          const res = await this.$store.dispatch(`${ this.inStore }/request`, { opt: { url } });
          const count = res?.count ?? res?.data?.length;

          if (count !== undefined) {
            this.viewCounts = { ...this.viewCounts, [query]: count };
          }
        } catch (e) {
          // Recorded as unanswerable, or every later trigger retries it
          this.viewCounts = { ...this.viewCounts, [query]: null };
        }
      }));
    },


    countUrl(filters) {
      const urlFor = this.$store.getters[`${ this.inStore }/urlFor`];

      return urlFor(this.schema.id, null, {
        pagination: {
          filters:              (this.listScopeFilters || []).concat(filters),
          projectsOrNamespaces: this.listScopeNamespaces,
          page:                 1,
          pageSize:             1,
        }
      });
    },


    /** The table's first report is its own default, kept so matching it is stored as no sort */
    recordSort(sorting) {
      if (!this.showTableViews || !sorting?.sortBy) {
        return;
      }

      const { sortBy, descending } = sorting;

      // A view sorting by a hidden column keeps asking; the fallback is not an edit
      const wanted = this.view.sort;

      if (wanted && !this.viewHeaders.some((header) => header.name === wanted)) {
        return;
      }

      // The table obliged; recording it as "no sort" would mark the view changed
      if (wanted === sortBy && !!this.view.sortDescending === !!descending) {
        return;
      }

      if (!this.defaultSort) {
        this.defaultSort = { sortBy, descending: !!descending };

        // The first report is the table's own sort, made before the view's was applied - even when it
        // is the same column the other way round. Apply the view's now that the default is known
        if (wanted) {
          this.$nextTick(() => this.applyViewSort());

          return;
        }
      }

      const isDefault = sortBy === this.defaultSort.sortBy && !!descending === this.defaultSort.descending;
      const sort = isDefault ? null : sortBy;

      if ((this.view.sort || null) === sort && !!this.view.sortDescending === !!descending) {
        return;
      }

      this.view = {
        ...this.view, sort, sortDescending: !!descending
      };
    },


    applyViewSort() {
      const table = this.$refs.table;

      if (!table || !this.defaultSort) {
        return;
      }

      const wanted = this.view.sort;
      const shown = wanted && this.viewHeaders.some((header) => header.name === wanted);
      const sortBy = shown ? wanted : this.defaultSort.sortBy;
      const descending = shown ? !!this.view.sortDescending : this.defaultSort.descending;

      if (table.sortBy === sortBy && !!table.descending === descending) {
        return;
      }

      table.changeSort(sortBy, descending);
    },


    /**
     * Every row the current filter matches, not just the page on screen
     *
     * @param onProgress called with (done, total) after each page arrives
     * @param limit most rows to fetch
     */
    async allMatchingRows(onProgress, limit = EXPORT_ROW_LIMIT) {
      if (!this.externalPaginationEnabled || !this.externalPaginationArgs || !this.schema) {
        return this.viewRows;
      }

      // Whatever arrived before a failure is still written; only an export that got nowhere falls
      // back
      return this.fetchEveryPage(this.externalPaginationArgs, onProgress, limit, () => this.viewRows);
    },


    /**
     * Every row a request matches, a page at a time. `transient`, so the store and the table are
     * left alone, and pinned to the first page's revision so no row is dropped or repeated between
     * pages
     *
     * @param pagination what to ask for; the page and its size are filled in here
     * @param onProgress called with (done, total) after each page arrives
     * @param limit most rows to fetch
     * @param fallback answered if the first page fails; without one the failure is thrown
     */
    async fetchEveryPage(pagination, onProgress, limit, fallback) {
      const rows = [];
      let total = null;
      let revision;

      try {
        for (let page = 1; rows.length < limit; page++) {
          const res = await this.$store.dispatch(`${ this.inStore }/findPage`, {
            type: this.schema.id,
            opt:  {
              transient:  true,
              watch:      false,
              revision,
              pagination: {
                ...pagination,
                page,
                pageSize: EXPORT_PAGE_SIZE,
              },
            }
          });

          const data = res?.data || [];

          rows.push(...data);

          if (total === null) {
            total = Math.min(res?.pagination?.result?.count ?? data.length, limit);
            revision = res?.pagination?.result?.revision;
          }

          onProgress?.(Math.min(rows.length, total), total);

          // A short page is the end, whatever the count said
          if (data.length < EXPORT_PAGE_SIZE || rows.length >= total) {
            break;
          }
        }
      } catch (e) {
        if (!rows.length) {
          if (fallback) {
            return fallback();
          }

          throw e;
        }
      }

      return rows;
    },


    /** The rows a tab not on screen matches, worked out as opening it would, in its order */
    async rowsForView(view, onProgress, limit) {
      const parsed = this.parseViewQuery(view.query || '');
      let rows;

      if (this.serverSideTableViews) {
        // Terms the api can't answer are left out, as they are when the tab is open
        const { filters } = queryToServerFilters(parsed, this.viewQueryFields);

        // Asked in the view's order, as the table asks for its page
        const { fields, descending } = this.sortFor(view);

        rows = await this.fetchEveryPage({
          filters:              (this.listScopeFilters || []).concat(filters),
          projectsOrNamespaces: this.listScopeNamespaces,
          sort:                 fields.map((field) => ({ field, asc: !descending })),
        }, onProgress, limit);
      } else {
        rows = parsed.clauses.length ? applyQueryExpression(this.filteredRows, parsed, this.viewQueryFields) : this.filteredRows;
      }

      // The api has ordered a server side list's already
      return this.serverSideTableViews ? rows : this.orderRowsFor(view, rows);
    },


    /**
     * How the table would order `view`: its grouping, its sort column (or the table's default), then
     * the tie breakers
     */
    sortFor(view) {
      const headers = this.headersForView(view);
      const own = !!view.sort && headers.some((header) => header?.name === view.sort);
      const sortName = own ? view.sort : this.defaultSort?.sortBy;
      const descending = own ? !!view.sortDescending : !!this.defaultSort?.descending;
      const column = sortName ? headers.find((header) => header?.name?.toLowerCase() === sortName.toLowerCase()) : null;
      const fromColumn = typeof column?.sort === 'string' || Array.isArray(column?.sort) ? [].concat(column.sort) : [];
      const groupBy = this.groupByOf(view);
      const group = groupBy ? this.groupSortFor(this.groupFieldFor(groupBy)) : null;
      const fields = uniq([].concat(group || [], fromColumn).concat(this._mandatorySort || DEFAULT_MANDATORY_SORT));

      return { fields, descending };
    },


    /** In the order the table would show `view` - see sortFor */
    orderRowsFor(view, rows) {
      const { fields, descending } = this.sortFor(view);

      return sortBy(rows, fields, descending);
    },


    /** A tab's view arrives whole, so its query applies at once rather than waiting as typing does */
    openTabView(view) {
      this.debouncedSettleQuery.cancel();
      this.settledQuery = view?.query || '';
      this.view = view;
      this.tabViewChanges += 1;
    },


    beginViewSwitch() {
      // Kept from the first change of a switch: a second one in the same switch starts no new wait
      if (!this.viewSwitching) {
        this.viewSwitchFromArgs = this.externalPaginationArgs;
      }
      this.viewSwitching = true;
      clearTimeout(this.viewSwitchTimer);
      this.viewSwitchTimer = setTimeout(() => {
        this.viewSwitching = false;
      }, VIEW_SWITCH_TIMEOUT);
    },


    endViewSwitch() {
      if (!this.viewSwitching) {
        return;
      }

      clearTimeout(this.viewSwitchTimer);
      this.viewSwitching = false;
    },


    /**
     * Export every row the view matches, tracked as a task in the notification centre. The store is
     * dispatched to directly: the shell api can't turn a task into a success
     *
     * @param {{ format: string, name: string, view?: Partial<import('@shell/types/table-views').TableViewState> }} args
     *   `view` is a tab other than the one on screen
     */
    async handleExport({ format, name, view }) {
      const viewName = name || this.t('tableViews.tabs.all');
      const id = await this.$store.dispatch('notifications/add', {
        level:    NotificationLevel.Task,
        title:    this.t('tableViews.export.notification.title'),
        message:  this.t('tableViews.export.notification.message', { name: viewName, format: (format || '').toUpperCase() }),
        progress: 0,
      });

      // How much of the progress bar fetching takes; YAML still has a request per resource to come
      const fetchShare = format === 'yaml' ? 20 : 90;
      const report = (done, total, from, to) => {
        this.$store.dispatch('notifications/update', { id, progress: Math.round(from + (((to - from) * done) / (total || 1))) });
      };

      try {
        const limit = format === 'yaml' ? EXPORT_ROW_LIMIT_YAML : EXPORT_ROW_LIMIT;
        const onFetch = (done, total) => report(done, total, 0, fetchShare);
        const rows = view ? await this.rowsForView(view, onFetch, limit) : await this.allMatchingRows(onFetch, limit);

        if (!rows.length || !format) {
          return this.$store.dispatch('notifications/remove', id);
        }

        const columns = view ? exportColumnsFor(this.headersForView(view), (key) => this.t(key)) : this.exportColumns;
        const file = await this.writeExport(rows, format, (done, total) => report(done, total, fetchShare, 100), columns);

        return this.$store.dispatch('notifications/update', {
          id,
          level:    NotificationLevel.Success,
          title:    this.t('tableViews.export.notification.doneTitle'),
          message:  this.t('tableViews.export.notification.doneMessage', { name: viewName, file }),
          progress: 100,
        });
      } catch (e) {
        console.error('Unable to export the view', e); // eslint-disable-line no-console

        return this.$store.dispatch('notifications/update', {
          id,
          level:   NotificationLevel.Error,
          title:   this.t('tableViews.export.notification.failedTitle'),
          message: this.t('tableViews.export.notification.failedMessage', { name: viewName }),
        });
      }
    },


    /**
     * Write the rows to a file and download it. YAML is the resources themselves, via the Download
     * YAML action
     */
    async writeExport(rows, format, onProgress, columns = this.exportColumns) {
      if (format === 'yaml' && typeof rows[0]?.downloadYaml === 'function') {
        if (rows.length === 1) {
          await rows[0].downloadYaml();

          return `${ rows[0].nameDisplay }.yaml`;
        }

        await rows[0].downloadYamlBulk(rows, onProgress);

        return 'resources.zip';
      }

      const name = (this.schema?.id || 'resources').replace(/[^a-z0-9]+/gi, '-');
      const writers = {
        yaml: { write: rowsToYaml, type: 'application/yaml;charset=utf-8' },
        json: { write: rowsToJson, type: 'application/json;charset=utf-8' },
        csv:  { write: rowsToCsv, type: 'text/csv;charset=utf-8' },
      };
      const writer = writers[format] || writers.csv;
      const file = `${ name }.${ format }`;

      await downloadFile(file, writer.write(rows, columns), writer.type);

      return file;
    },
  },
};
